export const portAConfig = {
    PIN: 0x20,
    DDR: 0x21,
    PORT: 0x22,
};
export const portBConfig = {
    PIN: 0x23,
    DDR: 0x24,
    PORT: 0x25,
};
export const portCConfig = {
    PIN: 0x26,
    DDR: 0x27,
    PORT: 0x28,
};
export const portDConfig = {
    PIN: 0x29,
    DDR: 0x2a,
    PORT: 0x2b,
};
export const portEConfig = {
    PIN: 0x2c,
    DDR: 0x2d,
    PORT: 0x2e,
};
export const portFConfig = {
    PIN: 0x2f,
    DDR: 0x30,
    PORT: 0x31,
};
export const portGConfig = {
    PIN: 0x32,
    DDR: 0x33,
    PORT: 0x34,
};
export const portHConfig = {
    PIN: 0x100,
    DDR: 0x101,
    PORT: 0x102,
};
export const portJConfig = {
    PIN: 0x103,
    DDR: 0x104,
    PORT: 0x105,
};
export const portKConfig = {
    PIN: 0x106,
    DDR: 0x107,
    PORT: 0x108,
};
export const portLConfig = {
    PIN: 0x109,
    DDR: 0x10a,
    PORT: 0x10b,
};
export var PinState;
(function (PinState) {
    PinState[PinState["Low"] = 0] = "Low";
    PinState[PinState["High"] = 1] = "High";
    PinState[PinState["Input"] = 2] = "Input";
    PinState[PinState["InputPullUp"] = 3] = "InputPullUp";
})(PinState || (PinState = {}));
/* This mechanism allows timers to override specific GPIO pins */
export var PinOverrideMode;
(function (PinOverrideMode) {
    PinOverrideMode[PinOverrideMode["None"] = 0] = "None";
    PinOverrideMode[PinOverrideMode["Enable"] = 1] = "Enable";
    PinOverrideMode[PinOverrideMode["Set"] = 2] = "Set";
    PinOverrideMode[PinOverrideMode["Clear"] = 3] = "Clear";
    PinOverrideMode[PinOverrideMode["Toggle"] = 4] = "Toggle";
})(PinOverrideMode || (PinOverrideMode = {}));
export class AVRIOPort {
    constructor(cpu, portConfig) {
        this.cpu = cpu;
        this.portConfig = portConfig;
        this.listeners = [];
        this.pinValue = 0;
        this.overrideMask = 0xff;
        this.lastValue = 0;
        this.lastDdr = 0;
        cpu.writeHooks[portConfig.DDR] = (value) => {
            const portValue = cpu.data[portConfig.PORT];
            cpu.data[portConfig.DDR] = value;
            this.updatePinRegister(portValue, value);
            this.writeGpio(portValue, value);
            return true;
        };
        cpu.writeHooks[portConfig.PORT] = (value) => {
            const ddrMask = cpu.data[portConfig.DDR];
            cpu.data[portConfig.PORT] = value;
            this.updatePinRegister(value, ddrMask);
            this.writeGpio(value, ddrMask);
            return true;
        };
        cpu.writeHooks[portConfig.PIN] = (value) => {
            // Writing to 1 PIN toggles PORT bits
            const oldPortValue = cpu.data[portConfig.PORT];
            const ddrMask = cpu.data[portConfig.DDR];
            const portValue = oldPortValue ^ value;
            cpu.data[portConfig.PORT] = portValue;
            cpu.data[portConfig.PIN] = (cpu.data[portConfig.PIN] & ~ddrMask) | (portValue & ddrMask);
            this.writeGpio(portValue, ddrMask);
            return true;
        };
        // The following hook is used by the timer compare output to override GPIO pins:
        cpu.gpioTimerHooks[portConfig.PORT] = (pin, mode) => {
            const pinMask = 1 << pin;
            if (mode == PinOverrideMode.None) {
                this.overrideMask |= pinMask;
            }
            else {
                this.overrideMask &= ~pinMask;
                switch (mode) {
                    case PinOverrideMode.Enable:
                        this.overrideValue &= ~pinMask;
                        this.overrideValue |= cpu.data[portConfig.PORT] & pinMask;
                        break;
                    case PinOverrideMode.Set:
                        this.overrideValue |= pinMask;
                        break;
                    case PinOverrideMode.Clear:
                        this.overrideValue &= ~pinMask;
                        break;
                    case PinOverrideMode.Toggle:
                        this.overrideValue ^= pinMask;
                        break;
                }
            }
            this.writeGpio(cpu.data[portConfig.PORT], cpu.data[portConfig.DDR]);
        };
    }
    addListener(listener) {
        this.listeners.push(listener);
    }
    removeListener(listener) {
        this.listeners = this.listeners.filter((l) => l !== listener);
    }
    /**
     * Get the state of a given GPIO pin
     *
     * @param index Pin index to return from 0 to 7
     * @returns PinState.Low or PinState.High if the pin is set to output, PinState.Input if the pin is set
     *   to input, and PinState.InputPullUp if the pin is set to input and the internal pull-up resistor has
     *   been enabled.
     */
    pinState(index) {
        const ddr = this.cpu.data[this.portConfig.DDR];
        const port = this.cpu.data[this.portConfig.PORT];
        const bitMask = 1 << index;
        if (ddr & bitMask) {
            return this.lastValue & bitMask ? PinState.High : PinState.Low;
        }
        else {
            return port & bitMask ? PinState.InputPullUp : PinState.Input;
        }
    }
    /**
     * Sets the input value for the given pin. This is the value that
     * will be returned when reading from the PIN register.
     */
    setPin(index, value) {
        const bitMask = 1 << index;
        this.pinValue &= ~bitMask;
        if (value) {
            this.pinValue |= bitMask;
        }
        this.updatePinRegister(this.cpu.data[this.portConfig.PORT], this.cpu.data[this.portConfig.DDR]);
    }
    updatePinRegister(port, ddr) {
        this.cpu.data[this.portConfig.PIN] = (this.pinValue & ~ddr) | (port & ddr);
    }
    writeGpio(value, ddr) {
        const newValue = ((value & this.overrideMask) | this.overrideValue) & ddr;
        const prevValue = this.lastValue;
        if (newValue !== prevValue || ddr !== this.lastDdr) {
            this.lastValue = newValue;
            this.lastDdr = ddr;
            for (const listener of this.listeners) {
                listener(newValue, prevValue);
            }
        }
    }
}
